import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';
import { jobApi, applicationApi } from '../services/api';
import { toast } from 'react-toastify';

const BATCH = 5;
async function runAutoCleanup(user) {
  const jobsDays = Math.max(1, parseInt(user.auto_cleanup_jobs_days, 10) || 90);
  const applicationsDays = Math.max(1, parseInt(user.auto_cleanup_applications_days, 10) || 90);
  try {
    const [jobsRes, applicationsRes] = await Promise.all([
      jobApi.getAllJobs().catch(() => ({ data: [] })),
      applicationApi.getAllApplications().catch(() => ({ data: [] }))
    ]);
    const jobs = Array.isArray(jobsRes.data) ? jobsRes.data : [];
    const applications = Array.isArray(applicationsRes.data) ? applicationsRes.data : [];
    const appliedJobIds = new Set(applications.map(a => String(a.jobId)));
    const jobsCutoff = Date.now() - jobsDays * 24 * 60 * 60 * 1000;
    const applicationsCutoff = Date.now() - applicationsDays * 24 * 60 * 60 * 1000;
    const toDeleteJobs = jobs.filter(j => {
      if (appliedJobIds.has(String(j.id))) return false;
      const created = j.createdAt ? new Date(j.createdAt).getTime() : 0;
      return created && created < jobsCutoff;
    });
    const toRejectApps = applications.filter(a => {
      if (a.status !== 'applied') return false;
      const appliedAt = a.appliedAt ? new Date(a.appliedAt).getTime() : 0;
      return appliedAt && appliedAt < applicationsCutoff;
    });
    for (let i = 0; i < toDeleteJobs.length; i += BATCH) {
      const batch = toDeleteJobs.slice(i, i + BATCH);
      await Promise.all(batch.map(j => jobApi.deleteJob(j.id).catch(() => {})));
    }
    for (let i = 0; i < toRejectApps.length; i += BATCH) {
      const batch = toRejectApps.slice(i, i + BATCH);
      await Promise.all(batch.map(a => applicationApi.updateApplication(a.id, { status: 'rejected' }).catch(() => {})));
    }
  } catch (e) {
    console.warn('Auto cleanup failed:', e);
  }
}

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Safely get token from localStorage (may throw in private browsing)
  const getStoredToken = () => {
    try {
      return localStorage.getItem('token') || null;
    } catch (e) {
      return null;
    }
  };
  const [token, setToken] = useState(getStoredToken);

  useEffect(() => {
    // Check if user is logged in on initial load
    const checkAuthStatus = async () => {
      if (token) {
        try {
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          const response = await api.get('/api/auth/me');
          
          // Store user data including admin status
          const userData = response.data;
          
          // Make sure is_admin is properly set based on the response
          // This ensures we can check for admin privileges in the UI
          if (userData.membership_type === 'admin') {
            userData.is_admin = true;
          }
          
          setCurrentUser(userData);
          setIsAuthenticated(true);
          if (userData.auto_cleanup_enabled) {
            setTimeout(() => runAutoCleanup(userData), 500);
          }
        } catch (error) {
          console.error('Authentication check failed:', error);
          // If token is invalid or expired, clean up
          localStorage.removeItem('token');
          setToken(null);
          setIsAuthenticated(false);
          setCurrentUser(null);
        }
      }
      setLoading(false);
    };

    checkAuthStatus();
  }, [token]);

  const login = async (email, password) => {
    if (!email || !password) {
      toast.error('Please enter both email and password');
      return false;
    }
    
    try {
      // Use URLSearchParams to send proper application/x-www-form-urlencoded body
      const params = new URLSearchParams();
      params.append('username', email);
      params.append('password', password);

      const response = await api.post('/api/auth/login', params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });
      
      const { access_token, user_id, full_name } = response.data;
      
      // Save tokens to localStorage and state
      localStorage.setItem('token', access_token);
      setToken(access_token);
      
      // Set default authorization header for all requests
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Get full user profile
      const userResponse = await api.get('/api/auth/me');
      const userData = userResponse.data;
      setCurrentUser(userData);
      setIsAuthenticated(true);
      if (userData.auto_cleanup_enabled) {
        setTimeout(() => runAutoCleanup(userData), 500);
      }
      toast.success('Login successful!');
      
      // Return the redirect URL if it exists
      const redirectUrl = localStorage.getItem('redirectAfterLogin');
      if (redirectUrl) {
        localStorage.removeItem('redirectAfterLogin');
        return { success: true, redirectUrl };
      }
      
      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      toast.error(error.response?.data?.detail || 'Login failed. Please check your credentials.');
      return { success: false };
    }
  };
  
  const googleLogin = async (credential) => {
    try {
      const response = await api.post('/api/auth/google-login', { credential });
      
      // Check if the user exists
      if (response.data.user_exists) {
        const { access_token } = response.data;
        
        // Save tokens to localStorage and state
        localStorage.setItem('token', access_token);
        setToken(access_token);
        
        // Set default authorization header for all requests
        api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        
        // Get full user profile
        const userResponse = await api.get('/api/auth/me');
        const userData = userResponse.data;
        setCurrentUser(userData);
        setIsAuthenticated(true);
        if (userData.auto_cleanup_enabled) {
          setTimeout(() => runAutoCleanup(userData), 500);
        }
        toast.success('Google login successful!');
        
        // Return the redirect URL if it exists
        const redirectUrl = localStorage.getItem('redirectAfterLogin');
        if (redirectUrl) {
          localStorage.removeItem('redirectAfterLogin');
          return { success: true, redirectUrl };
        }
        
        return { success: true };
      } else {
        // User doesn't exist, return the email for registration
        return { 
          success: false, 
          needsRegistration: true, 
          email: response.data.email,
          name: response.data.name || ''
        };
      }
    } catch (error) {
      console.error('Google login failed:', error);
      toast.error(error.response?.data?.detail || 'Google login failed. Please try again.');
      return { success: false, needsRegistration: false };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post('/api/auth/register', userData);
      toast.success('Registration successful! Please log in.');
      return true;
    } catch (error) {
      console.error('Registration failed:', error);
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
      return false;
    }
  };

  const refreshUser = async () => {
    if (!token) return;
    try {
      const response = await api.get('/api/auth/me');
      setCurrentUser(response.data);
    } catch (e) {
      console.warn('Refresh user failed', e);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setCurrentUser(null);
    setIsAuthenticated(false);
    delete api.defaults.headers.common['Authorization'];
    toast.info('You have been logged out');
  };

  const linkedinLogin = async (code) => {
    try {
      // Define the redirect URI - must match what was used in the authorization request
      const currentPath = window.location.pathname;
      const redirectUri = currentPath === '/extension-auth' 
        ? `${window.location.origin}/extension-auth`
        : `${window.location.origin}/login`;
      
      // Use the authApi from services/api.js instead of direct API call
      // This ensures consistent error handling and request formatting
      const response = await api.post('/api/auth/linkedin-login', { 
        code,
        redirect_uri: redirectUri
      });
      
      // Check if the user exists
      if (response.data.user_exists) {
        const { access_token } = response.data;
        
        // Save tokens to localStorage and state
        localStorage.setItem('token', access_token);
        setToken(access_token);
        
        // Set default authorization header for all requests
        api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        
        // Get full user profile
        const userResponse = await api.get('/api/auth/me');
        const userData = userResponse.data;
        setCurrentUser(userData);
        setIsAuthenticated(true);
        if (userData.auto_cleanup_enabled) {
          setTimeout(() => runAutoCleanup(userData), 500);
        }
        toast.success('LinkedIn login successful!');
        
        // Return the redirect URL if it exists
        const redirectUrl = localStorage.getItem('redirectAfterLogin');
        if (redirectUrl) {
          localStorage.removeItem('redirectAfterLogin');
          return { success: true, redirectUrl };
        }
        
        return { success: true };
      } else {
        // User doesn't exist, return the email for registration
        return { 
          success: false, 
          needsRegistration: true, 
          email: response.data.email,
          name: response.data.name || ''
        };
      }
    } catch (error) {
      console.error('LinkedIn login failed:', error);
      // Provide more detailed error message if available
      const errorMessage = error.response?.data?.detail || 
                          (error.response?.data?.message) || 
                          'LinkedIn login failed. Please try again.';
      toast.error(errorMessage);
      return { success: false, needsRegistration: false };
    }
  };

  const value = {
    currentUser,
    isAuthenticated,
    loading,
    login,
    register,
    logout,
    googleLogin,
    linkedinLogin,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};